import { HttpResponse } from '@angular/common/http';
import { Component, input } from '@angular/core';
import { ToastService } from '@app/core/services/toast.service';
import { DeliveryNotes } from '@app/shared/models/business-model/delivery-notes';
import { ServiceEndpoints } from '@app/shared/models/service-model/service-endpoints';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { DeliveryNote } from 'api/models';
import {
    CheckinsService,
    DeliveryNotesService,
    PdfService,
} from 'api/services';
import { NgxSpinnerService } from 'ngx-spinner';
import { lastValueFrom } from 'rxjs';
import { OrgType } from '../delivery-notes/delivery-notes.component';
import { PdfService as PdfServiceAddons } from 'api/addons/services';
import {
    hasConsigneePdf,
    hasConsignorPdf,
} from '@app/core/stores/permissionsStore';
import { StrictHttpResponse } from '../../../../api/strict-http-response';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-download-note-button',
    templateUrl: './download-note-button.component.html',
    styleUrls: ['./download-note-button.component.scss'],
    imports: [MatIconButton, MatTooltip, MatIcon, TranslateModule],
})
export class DownloadNoteButtonComponent {
    deliveryNote = input.required<DeliveryNotes | DeliveryNote>();
    organizationSiteKey = input.required<string>();
    orgType = input<OrgType>(OrgType.Consignor);
    fetchCompletePDF = input<boolean>();

    constructor(
        private spinner: NgxSpinnerService,
        private toastr: ToastService,
        private translate: TranslateService,
        private deliveryNotesService: DeliveryNotesService,
        private checkinService: CheckinsService,
        private pdfService: PdfService,
        private pdfServiceAddons: PdfServiceAddons
    ) {}

    get shouldFetchSigned(): boolean {
        const deliveryNote = this.deliveryNote();
        if (
            'deliveryNoteBundleKey' in deliveryNote &&
            !deliveryNote.deliveryNoteBundleKey
        )
            return false;

        if (!('deliveryNoteBundleStatus' in deliveryNote)) return false;

        return (
            deliveryNote.deliveryNoteBundleStatus === 'sent' ||
            deliveryNote.deliveryNoteBundleStatus === 'closed'
        );
    }

    private downloadBlob(blob: Blob, filename: string): void {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.setAttribute('style', 'display:none;');
        document.body.appendChild(a);
        a.href = url;
        a.download = filename;
        a.click();
    }

    async downloadDeliveryNote(): Promise<void> {
        try {
            await this.spinner.show();
            await this.downloadDeliveryNoteWithoutBundleKey();
        } catch (error) {
            console.error(error);
            this.toastr.error(this.translate.instant('ERROR.DOWNLOAD-NOTE'));
        } finally {
            this.spinner.hide();
        }
    }

    async downloadDeliveryNoteWithoutBundleKey(): Promise<void> {
        try {
            let blobData: Blob | undefined;
            let responseHeaders: HttpResponse<Blob>['headers'] | undefined;

            switch (this.orgType()) {
                case OrgType.Consignor:
                    if (this.fetchCompletePDF()) {
                        let response: StrictHttpResponse<Blob>;
                        if (hasConsignorPdf()) {
                            response = await lastValueFrom(
                                this.pdfServiceAddons.getConsignorDeliveryNoteCombinedPdf$Response(
                                    {
                                        organizationSiteKey:
                                            this.organizationSiteKey(),
                                        deliveryNoteKey:
                                            this.deliveryNote()!._key,
                                    }
                                )
                            );
                        } else {
                            response = await lastValueFrom(
                                this.pdfService.getConsignorDeliveryNoteCombinedPdf$Response(
                                    {
                                        organizationSiteKey:
                                            this.organizationSiteKey(),
                                        deliveryNoteKey:
                                            this.deliveryNote()!._key,
                                    }
                                )
                            );
                        }
                        blobData = response.body;
                        responseHeaders = response.headers;
                    } else {
                        const response = await lastValueFrom(
                            this.deliveryNotesService.getDeliveryNoteFromPool$Response(
                                {
                                    organizationSiteKey:
                                        this.organizationSiteKey(),
                                    deliveryNoteKey: this.deliveryNote()!._key,
                                }
                            )
                        );
                        blobData = response.body;
                        responseHeaders = response.headers;
                    }
                    break;
                case OrgType.Consignee: {
                    let response: StrictHttpResponse<Blob>;
                    if (hasConsigneePdf()) {
                        response = await lastValueFrom(
                            this.pdfServiceAddons.getConsigneeDeliveryNoteCombinedPdf$Response(
                                {
                                    organizationSiteKey:
                                        this.organizationSiteKey(),
                                    deliveryNoteKey: this.deliveryNote()!._key,
                                }
                            )
                        );
                    } else {
                        response = await lastValueFrom(
                            this.checkinService.getConsigneeDeliveryNoteCombinedPdf$Response(
                                {
                                    organizationSiteKey:
                                        this.organizationSiteKey(),
                                    deliveryNoteKey: this.deliveryNote()!._key,
                                }
                            )
                        );
                    }
                    // Convert ArrayBuffer to Blob directly with PDF mime type
                    const buffer = await response.body.arrayBuffer();
                    blobData = new Blob([buffer], { type: 'application/pdf' });
                    responseHeaders = response.headers;
                    break;
                }
                case OrgType.Carrier: {
                    const data = await lastValueFrom(
                        this.pdfService.getCarrierDeliveryNoteCombinedPdf$Response(
                            {
                                organizationSiteKey: this.organizationSiteKey(),
                                deliveryNoteKey: this.deliveryNote()!._key,
                            }
                        )
                    );
                    const carrierBuffer = await data.body.arrayBuffer();
                    blobData = new Blob([carrierBuffer], {
                        type: 'application/pdf',
                    });
                    responseHeaders = data.headers;
                    break;
                }
            }

            if (!blobData) throw new Error('No blob data found');

            // the downloadable pdf is renamed serverside eg. glnNumber_deliverynoteNumber.pdf, the new filename is part of the reponse header
            const glnFilename: string =
                responseHeaders?.get('content-disposition')?.split('=')[1] ||
                this.deliveryNote()!.fileName;

            this.downloadBlob(blobData, glnFilename);
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    async downloadDeliveryNoteWithBundleKey(): Promise<void> {
        if (!('deliveryNoteBundleKey' in this.deliveryNote())) return;
        try {
            let response: HttpResponse<Blob>;
            let endpointToUse: string;

            /*
            switch (this.orgType()) {
                case OrgType.Consignor:
                    endpointToUse = this.shouldFetchSigned
                        ? ServiceEndpoints.getDeliveryNoteSignedPdfbyKey
                        : ServiceEndpoints.getDeliveryNotePdfbyKey;
                    break;
                case OrgType.Consignee:
                    endpointToUse = this.shouldFetchSigned
                        ? ServiceEndpoints.getDeliveryNoteSignedPdfbyKeyConsignee
                        : ServiceEndpoints.getDeliveryNotePdfbyKeyConsignee;
                    break;
                case OrgType.Carrier:
                    endpointToUse =
                        ServiceEndpoints.getDeliveryNotePdfbyKeyCarrier;
            }

            if (!endpointToUse) throw new Error('No endpoint found');

            response = await lastValueFrom(
                this.deliveryNotesService.getDeliveryNoteInBundleByKeyFull(
                    this.organizationSiteKey(),
                    this.deliveryNote()!.deliveryNoteBundleKey as string,
                    this.deliveryNote()!._key,
                    endpointToUse
                )
            );

            if (response) {
                // the downloadable pdf is renamed serverside eg. glnNumber_deliverynoteNumber.pdf, the new filename is part of the reponse header
                const glnFilename: string =
                    response.headers
                        .get('content-disposition')
                        ?.split('=')[1] || this.deliveryNote()!.fileName;

                const blob = response.body as Blob;

                this.downloadBlob(blob, glnFilename);

            // TODO
            }
            */
        } catch (error) {
            throw error;
        } finally {
            this.spinner.hide();
        }
    }
}
